import { useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import useSWR from 'swr'
import { useTranslation } from 'react-i18next'
import { ArchiveRestore, ChevronRight, Plus, Settings } from 'lucide-react'
import { Button, cn, Switch, Tooltip } from '@ludus/ui'
import type { Pipeline } from '@ludus/core'
import { listPipelines, setPipelineActive, PipelineApiError } from '@/lib/api/pipelines.ts'
import { CenteredPanel } from '@/shared/feedback/CenteredPanel.tsx'
import { pipelineAccent } from '@/shared/utils/pipelineAccent.ts'
import { NewPipelineDialog } from '../components/NewPipelineDialog.tsx'

/**
 * Pipelines catalogue (SBO-59). Lists every active pipeline registered for the
 * tenant via `GET /api/pipelines` as a responsive card grid. Each card links to
 * its kanban (`/pipelines/:id`) and carries a stable per-pipeline accent
 * (`pipelineAccent`) so the screen reads as a deliberate set rather than a stack
 * of identical rows. The list always renders — even with a single pipeline — so
 * the breadcrumb hierarchy stays honest.
 */
export function PipelinesListPage() {
  const { t } = useTranslation()
  const [showArchived, setShowArchived] = useState(false)
  // Archiving hides a board from every ordinary reader, so without this view an
  // archived pipeline would be unreachable — and archiving a one-way trip.
  const { data, isLoading, error, mutate } = useSWR<Pipeline[], PipelineApiError>(
    showArchived ? '/pipelines?includeArchived=true' : '/pipelines',
    () => listPipelines({ includeArchived: showArchived }),
  )
  const [creating, setCreating] = useState(false)

  if (isLoading) {
    return (
      <CenteredPanel
        title={t('pages.pipelines.list.loading', { defaultValue: 'Loading pipelines…' })}
        muted
      />
    )
  }
  if (error) {
    return (
      <CenteredPanel
        title={t('pages.pipelines.list.error.title', { defaultValue: 'Could not load pipelines' })}
        body={
          error.kind === 'unauthorized'
            ? t('pages.pipelines.list.error.unauthorized', {
                defaultValue:
                  'The console could not authenticate against the engine. Set a valid bearer token and reload.',
              })
            : t('pages.pipelines.list.error.generic', {
                defaultValue: 'The engine returned an error while listing pipelines.',
              })
        }
        meta={error.status !== null ? `HTTP ${error.status} · ${error.message}` : undefined}
        tone="error"
      />
    )
  }

  const pipelines = data ?? []

  return (
    <div className="flex w-full max-w-5xl flex-1 flex-col gap-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border/60 pb-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-foreground">
            {t('pages.pipelines.list.title', { defaultValue: 'Pipelines' })}
          </h1>
          <p className="text-xs text-muted">
            {t('pages.pipelines.list.subtitle', {
              defaultValue: 'Select a pipeline to view its board, or configure its settings.',
            })}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-muted">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} />
            {t('pages.pipelines.list.showArchived', { defaultValue: 'Show archived' })}
          </label>
          {pipelines.length > 0 && <NewPipelineButton onClick={() => setCreating(true)} />}
        </div>
      </header>

      {pipelines.length === 0 ? (
        <CenteredPanel
          title={t('pages.pipelines.list.empty.title', { defaultValue: 'No pipelines yet' })}
          body={t('pages.pipelines.list.empty.body', {
            defaultValue:
              'Create the first one — it comes with a default set of stages you can change in its settings.',
          })}
          muted
        >
          <NewPipelineButton onClick={() => setCreating(true)} />
        </CenteredPanel>
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {pipelines.map((pipeline) => (
            <PipelineCard
              key={pipeline.id}
              pipeline={pipeline}
              viewBoardLabel={t('pages.pipelines.list.viewBoard', { defaultValue: 'View board' })}
              settingsLabel={t('pages.pipelines.list.settings', {
                defaultValue: 'Pipeline settings',
              })}
              onRestore={async () => {
                await setPipelineActive(pipeline.id, true)
                await mutate()
              }}
            />
          ))}
        </ul>
      )}

      <NewPipelineDialog open={creating} onOpenChange={setCreating} />
    </div>
  )
}

function NewPipelineButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <Button variant="primary" size="sm" onClick={onClick}>
      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
      {t('pages.pipelines.list.newPipeline', { defaultValue: 'New pipeline' })}
    </Button>
  )
}

interface PipelineCardProps {
  pipeline: Pipeline
  viewBoardLabel: string
  settingsLabel: string
  onRestore: () => Promise<void>
}

function PipelineCard({ pipeline, viewBoardLabel, settingsLabel, onRestore }: PipelineCardProps) {
  const { t } = useTranslation()
  const accent = pipelineAccent(pipeline)
  const initial = pipeline.name.trim().charAt(0).toUpperCase() || '•'

  return (
    <li className="list-none">
      <div className={cn('group relative animate-fade-in', !pipeline.isActive && 'opacity-60')}>
        <Link
          to={`/pipelines/${pipeline.id}`}
          style={{ '--accent': accent.hex } as CSSProperties}
          className="relative flex min-h-[7.5rem] flex-col justify-between gap-6 overflow-hidden rounded-xl border border-border bg-background-secondary p-5 outline-none transition-transform duration-200 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
        >
          {/* Accent corner glow — brightens on hover. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-70 transition-opacity duration-200 group-hover:opacity-100"
            style={{
              background: `radial-gradient(120% 90% at 0% 0%, ${accent.glow} 0%, transparent 60%)`,
            }}
          />
          {/* Left identity edge. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 w-[3px]"
            style={{ backgroundColor: accent.hex, opacity: 0.6 }}
          />
          {/* Accent inset ring, revealed on hover. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            style={{ boxShadow: `inset 0 0 0 1px ${accent.ring}` }}
          />

          <div className="relative flex items-center gap-3">
            <span
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-base font-semibold"
              style={{
                backgroundColor: accent.tile,
                color: accent.hex,
                boxShadow: `inset 0 0 0 1px ${accent.ring}`,
              }}
            >
              {initial}
            </span>
            <span className="min-w-0 flex-1 truncate pr-8 text-base font-semibold text-foreground">
              {pipeline.name}
            </span>
          </div>

          <span className="relative flex items-center gap-1.5 text-xs font-medium text-muted">
            {viewBoardLabel}
            <ChevronRight
              className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
              style={{ color: accent.hex }}
              aria-hidden="true"
            />
          </span>
        </Link>

        {/* Gear sits outside the card <Link> so it's a separate, non-navigating
            affordance — it routes to the pipeline's settings (SBO-60). */}
        <div className="absolute right-3 top-3 flex items-center gap-1">
          {!pipeline.isActive && (
            <Tooltip
              content={t('pages.pipelines.list.restore', { defaultValue: 'Restore from archive' })}
              side="left"
            >
              <Button variant="ghost" size="sm" onClick={() => void onRestore()}>
                <ArchiveRestore className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </Tooltip>
          )}
          <Tooltip content={settingsLabel} side="left">
            <Link
              to={`/pipelines/${pipeline.id}/settings`}
              aria-label={settingsLabel}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted outline-none transition-colors hover:bg-sidebar-hover-bg hover:text-foreground focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
              style={{ '--accent': accent.hex } as CSSProperties}
            >
              <Settings className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Tooltip>
        </div>
      </div>
    </li>
  )
}
